<script setup lang="ts">
import { useIsMobile } from '@/composables/useMediaQuery'
import type { Lecture } from '@/types/lecture.types'
import { computed } from 'vue'

const props = defineProps<{
  lecture: Lecture
  selected?: boolean
}>()

const isMobile = useIsMobile()

const vimeoUrl = computed(() => {
  const baseUrl = props.lecture.thumbnailVimeoUrl
  const hasParams = baseUrl.includes('?')
  return `${baseUrl}${hasParams ? '&' : '?'}muted=1&controls=0`
})
</script>

<template>
  <div class="last-video-item" :class="{ selected: selected }">
    <div class="last-video-item-image">
      <img v-if="props.lecture.thumbnailType !== 'VIDEO'" :src="props.lecture.thumbnailImageUrl" />
      <iframe
        v-if="props.lecture.thumbnailType === 'VIDEO' && props.lecture.thumbnailVimeoUrl"
        :id="`vimeo-player-thumbnail-${props.lecture.idx}`"
        :src="vimeoUrl"
        frameborder="0"
        allow="autoplay; fullscreen"
        allowfullscreen
      ></iframe>
    </div>
    <div class="last-video-item-info">
      <div class="last-video-item-level sub-md white" :class="{ 'body-sm': isMobile }">
        {{ props.lecture.level }}
      </div>
      <div class="last-video-item-title body-lg white">{{ props.lecture.name }}</div>
    </div>
  </div>
</template>

<style scoped>
.last-video-item {
  width: 413px;
  height: 134px;
  background-color: var(--gray-800);
  border-radius: 8px;
  padding: 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: start;
}

.last-video-item.selected {
  background-color: var(--tint-500);
}

.last-video-item-image {
  width: 196px;
  height: 110px;
  border-radius: 8px;
  margin-right: 12px;
  position: relative;
}

.last-video-item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.last-video-item-image iframe {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
  border-radius: 8px;
  pointer-events: none;
}

.last-video-item-info {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
}

@media (max-width: 1365px) {
  .last-video-item {
    width: 196px;
    height: 164px;
    background-color: transparent;
    flex-direction: column;
    align-items: start;
    justify-content: start;
    padding: 0;
  }

  .last-video-item.selected {
    background-color: transparent;
  }

  .last-video-item-image {
    width: 196px;
    height: 110px;
    object-fit: cover;
    margin-right: 0;
  }

  .last-video-item-image iframe {
    position: relative;
  }

  .last-video-item-info {
    margin-top: 8px;
  }

  .last-video-item-title {
    margin-bottom: 4px;
  }
}
</style>
